import React, { useState } from 'react';
import axios from 'axios';
import './taxwithheldcalculator.css';
import { API_BASE_URL, NGROK_URL } from '../../utils/const';

const INCOME_THRESHOLDS = {
    '2013-14': { 'lower': 25677, 'upper': 32279 },
    '2014-15': { 'lower': 26120, 'upper': 33044 },
    '2015-16': { 'lower': 26668, 'upper': 33738 },
    '2016-17': { 'lower': 27068, 'upper': 34244 },
    '2017-18': { 'lower': 27475, 'upper': 34758 },
    '2018-19': { 'lower': 27997, 'upper': 35418 },
    '2019-20': { 'lower': 28501, 'upper': 36056 },
    '2020-21': { 'lower': 29032, 'upper': 36705 },
    '2021-22': { 'lower': 29206, 'upper': 36925 },
    '2022-23': { 'lower': 30345, 'upper': 38365 },
    '2023-24': { 'lower': 32500, 'upper': 41089 },
    '2024-25': { 'lower': 34027, 'upper': 43037 }
  }
  

const MedicareLevyCalculator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [levyResult, setLevyResult] = useState(null);
  const [formData, setFormData] = useState({
    income_year: '2023-24',
    taxable_income: '',
    sapto_eligibility_choice: 'yes',
    dependent_children_ml_count: null,
    have_spouse_choice: null,
    spouse_taxable_income: null,
    exemption_claim_choice: null,
    exemption_type_choice: null,
    full_exemption_days: null,
    half_exemption_days: null
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    // For numeric fields, convert to number if applicable
    const processedValue = type === 'number' && value !== '' ? Number(value) : value;
    
    // Update the form data
    setFormData(prevData => {
      const updatedData = {
        ...prevData,
        [name]: processedValue
      };

      // Reset dependent fields when changing certain selections
      if (name === 'sapto_eligibility_choice' && value === 'yes') {
        return {
          ...updatedData,
          dependent_children_ml_count: null,
          have_spouse_choice: null,
          spouse_taxable_income: null,
          exemption_claim_choice: null
        };
      }
      
      if (name === 'have_spouse_choice' && value === 'no') {
        return {
          ...updatedData,
          spouse_taxable_income: null
        };
      }
      
      if (name === 'exemption_claim_choice' && value === 'no') {
        return {
          ...updatedData,
          exemption_type_choice: null,
          full_exemption_days: null,
          half_exemption_days: null
        };
      }
      
      if (name === 'exemption_type_choice') {
        if (value === 'full') {
          return {
            ...updatedData,
            half_exemption_days: null
          };
        } else if (value === 'half') {
          return {
            ...updatedData,
            full_exemption_days: null
          };
        }
      }
      
      return updatedData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Create a clean payload based on the current form state
      const payload = {
        income_year: formData.income_year,
        taxable_income: Number(formData.taxable_income),
        sapto_eligibility_choice: formData.sapto_eligibility_choice,
        dependent_children_ml_count: formData.sapto_eligibility_choice !== '' ? 
          Number(formData.dependent_children_ml_count) : null,
        have_spouse_choice: formData.sapto_eligibility_choice !== '' ? 
          formData.have_spouse_choice : null,
        spouse_taxable_income: formData.have_spouse_choice === 'yes' ? 
          Number(formData.spouse_taxable_income) : null,
        exemption_claim_choice: formData.sapto_eligibility_choice === 'no' ? 
          formData.exemption_claim_choice : null,
        exemption_type_choice: formData.exemption_claim_choice === 'yes' ? 
          formData.exemption_type_choice : null,
        full_exemption_days: ['full', 'both'].includes(formData.exemption_type_choice) ? 
          Number(formData.full_exemption_days) : null,
        half_exemption_days: ['half', 'both'].includes(formData.exemption_type_choice) ? 
          Number(formData.half_exemption_days) : null
      };

      // API call implementation
      const response = await axios.post(API_BASE_URL+'/ato_calculator/calculate-medicare-levy', payload);
      setLevyResult(response.data);
    } catch (error) {
      console.error('Error calculating Medicare levy:', error);
      // You could also set an error state here to display to the user
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="form-container py-5">
      <form className="p-4 border rounded bg-white shadow-sm" onSubmit={handleSubmit}>
        <h1 className="mb-3 text-primary">Medicare levy calculator</h1>
        <p className="note">All fields marked with * are mandatory</p>

        {/* Income Year Selection */}
        <div className="form-group mb-3">
          <label className="form-label">Select an income year: *</label>
          <select 
            className="form-control"
            name="income_year"
            value={formData.income_year}
            onChange={handleChange}
            required
          >
            {/* <option value="2023-24">2023-24</option> */}
            <option value="2013-14">2013–14</option>
            <option value="2014-15">2014–15</option>
            <option value="2015-16">2015–16</option>
            <option value="2016-17">2016–17</option>
            <option value="2017-18">2017–18</option>
            <option value="2018-19">2018–19</option>
            <option value="2019-20">2019–20</option>
            <option value="2020-21">2020–21</option>
            <option value="2021-22">2021–22</option>
            <option value="2022-23">2022–23</option>
            <option value="2023-24">2023–24</option>
          </select>
        </div>

        {/* Taxable Income */}
        <div className="form-group mb-3">
          <label className="form-label">Taxable income *</label>
          <div className="input-group">
            <span className="input-group-prepend">$</span>
            <input
              type="number"
              className="form-control"
              name="taxable_income"
              value={formData.taxable_income}
              onChange={handleChange}
              required
            />
            <span className="input-group-append">.00</span>
          </div>
        </div>

        {/* SAPTO Eligibility */}
        {+formData.taxable_income >= INCOME_THRESHOLDS[formData.income_year].lower && (
          <>
            <div className="form-group mb-3">
              <label>Were you eligible for the seniors and pensioners tax offset (SAPTO)? *</label>
              <div className="residency-options">
                <div className={`residency-option ${formData.sapto_eligibility_choice === 'yes' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    id="saptoYes"
                    name="sapto_eligibility_choice"
                    value="yes"
                    checked={formData.sapto_eligibility_choice === 'yes'}
                    onChange={handleChange}
                  />
                  <label htmlFor="saptoYes">Yes</label>
                </div>
                <div className={`residency-option ${formData.sapto_eligibility_choice === 'no' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    id="saptoNo"
                    name="sapto_eligibility_choice"
                    value="no"
                    checked={formData.sapto_eligibility_choice === 'no'}
                    onChange={handleChange}
                  />
                  <label htmlFor="saptoNo">No</label>
                </div>
              </div>
            </div>

            {/* If not eligible for SAPTO, show additional questions */}
            {((formData.sapto_eligibility_choice === 'no') || (formData.taxable_income >= INCOME_THRESHOLDS[formData.income_year].upper)) && (
              <>
                <div className="form-group mb-3">
                  <label className="form-label">How many dependent children and students did you have? *</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      name="dependent_children_ml_count"
                      value={formData.dependent_children_ml_count || ''}
                      onChange={handleChange}
                      min="0"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group mb-3">
                  <label>Did you have a spouse (married or de facto) on 30 June of the selected income year? *</label>
                  <div className="residency-options">
                    <div className={`residency-option ${formData.have_spouse_choice === 'yes' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        id="spouseYes"
                        name="have_spouse_choice"
                        value="yes"
                        checked={formData.have_spouse_choice === 'yes'}
                        onChange={handleChange}
                      />
                      <label htmlFor="spouseYes">Yes</label>
                    </div>
                    <div className={`residency-option ${formData.have_spouse_choice === 'no' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        id="spouseNo"
                        name="have_spouse_choice"
                        value="no"
                        checked={formData.have_spouse_choice === 'no'}
                        onChange={handleChange}
                      />
                      <label htmlFor="spouseNo">No</label>
                    </div>
                  </div>
                </div>
                
                {formData.have_spouse_choice === 'yes' && (
                  <div className="form-group mb-3">
                    <label className="form-label">Spouse's taxable income *</label>
                    <div className="input-group">
                      <span className="input-group-prepend">$</span>
                      <input
                        type="number"
                        className="form-control"
                        name="spouse_taxable_income"
                        value={formData.spouse_taxable_income || ''}
                        onChange={handleChange}
                        required
                      />
                      <span className="input-group-append">.00</span>
                    </div>
                  </div>
                )}
                
                {/* Medicare Levy Exemption */}
                <div className="form-group mb-3">
                  <label>Were you eligible for any Medicare levy exemption? *</label>
                  <div className="residency-options">
                    <div className={`residency-option ${formData.exemption_claim_choice === 'yes' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        id="exemptionYes"
                        name="exemption_claim_choice"
                        value="yes"
                        checked={formData.exemption_claim_choice === 'yes'}
                        onChange={handleChange}
                      />
                      <label htmlFor="exemptionYes">Yes</label>
                    </div>
                    <div className={`residency-option ${formData.exemption_claim_choice === 'no' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        id="exemptionNo"
                        name="exemption_claim_choice"
                        value="no"
                        checked={formData.exemption_claim_choice === 'no'}
                        onChange={handleChange}
                      />
                      <label htmlFor="exemptionNo">No</label>
                    </div>
                  </div>
                </div>
                
                {formData.exemption_claim_choice === 'yes' && (
                  <div className="form-group mb-3">
                    <label>Which type of Medicare levy exemption were you eligible for during the selected income year? *</label>
                    <div className="residency-options">
                      <div className={`residency-option ${formData.exemption_type_choice === 'full' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          id="exemptionTypeFull"
                          name="exemption_type_choice"
                          value="full"
                          checked={formData.exemption_type_choice === 'full'}
                          onChange={handleChange}
                        />
                        <label htmlFor="exemptionTypeFull">Full levy exemption only</label>
                      </div>
                      <div className={`residency-option ${formData.exemption_type_choice === 'half' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          id="exemptionTypeHalf"
                          name="exemption_type_choice"
                          value="half"
                          checked={formData.exemption_type_choice === 'half'}
                          onChange={handleChange}
                        />
                        <label htmlFor="exemptionTypeHalf">Half levy exemption only</label>
                      </div>
                      <div className={`residency-option ${formData.exemption_type_choice === 'both' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          id="exemptionTypeBoth"
                          name="exemption_type_choice"
                          value="both"
                          checked={formData.exemption_type_choice === 'both'}
                          onChange={handleChange}
                        />
                        <label htmlFor="exemptionTypeBoth">Both levy exemptions</label>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Exemption Days fields */}
                {formData.exemption_type_choice === 'full' && (
                  <div className="form-group mb-3">
                    <label className="form-label">Full levy exemption days *</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        name="full_exemption_days"
                        value={formData.full_exemption_days || ''}
                        onChange={handleChange}
                        min="0"
                        max="365"
                        required
                      />
                    </div>
                  </div>
                )}
                
                {formData.exemption_type_choice === 'half' && (
                  <div className="form-group mb-3">
                    <label className="form-label">Half levy exemption days *</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        name="half_exemption_days"
                        value={formData.half_exemption_days || ''}
                        onChange={handleChange}
                        min="0"
                        max="365"
                        required
                      />
                    </div>
                  </div>
                )}
                
                {formData.exemption_type_choice === 'both' && (
                  <div className="form-group">
                    <h4>Exempt Days</h4>
                    <div className="mb-3">
                      <label>Full levy exemption days *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="full_exemption_days"
                        value={formData.full_exemption_days || ''}
                        onChange={handleChange}
                        min="0"
                        max="365"
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label>Half levy exemption days *</label>
                      <input
                        type="number"
                        className="form-control"
                        name="half_exemption_days"
                        value={formData.half_exemption_days || ''}
                        onChange={handleChange}
                        min="0"
                        max="365"
                        required
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Submit Button */}
        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? 'Calculating...' : 'Calculate Medicare Levy'}
        </button>
      </form>

      {/* Results Section */}
      {levyResult && (
        <div className="results-section mt-4">
          <div className="tax-results p-3 border rounded">
            <h3>Medicare Levy Calculation Results</h3>
            <p className="mb-2">
              <strong>Medicare Levy Amount:</strong> {levyResult.medicare_levy_amount}
            </p>
            {levyResult.details && (
              <div className="calculation-details">
                <h4>Calculation Details</h4>
                <pre>{JSON.stringify(levyResult.details, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicareLevyCalculator;